import { type ReactNode } from 'react'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetHeader,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetListItem,
  ModalSheetSpacer,
} from '@/components/ModalSheet'
import styles from './ViewJobScopeChangeRequestDialog.module.css'
import { dateFormatters } from '@/utils/date-util'

export interface ViewJobScopeChangeRequestItem {
  id: string
  name: string
  quantity: number
}

export interface ViewJobScopeChangeRequestDialogProps {
  showing: boolean
  isAccepted: boolean
  isRejected: boolean
  isApplyFailed: boolean
  isPending: boolean
  isCreatedByAdmin: boolean
  latestResponseTime: Date | undefined
  items: ViewJobScopeChangeRequestItem[]
  rejectionReasonField: ReactNode
  onClose: () => unknown
}

export function ViewJobScopeChangeRequestDialog({
  showing,
  isAccepted,
  isRejected,
  isApplyFailed,
  isPending,
  isCreatedByAdmin,
  latestResponseTime,
  items,
  rejectionReasonField,
  onClose,
}: ViewJobScopeChangeRequestDialogProps) {
  return (
    <ModalSheet showing={showing} onRequestClose={onClose}>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          {isAccepted && (
            <>
              <ModalSheetContentTitle>
                <div className={styles.header}>Acknowledgment captured</div>
              </ModalSheetContentTitle>

              <ModalSheetContentDescription>
                <div className={styles.header}>
                  {isCreatedByAdmin
                    ? 'An admin updated the job scope on'
                    : 'A Customer Service Representative reviewed and accepted all job updates identified by you on'}{' '}
                  {dateFormatters.dateTimeDefault.format(latestResponseTime)}.
                </div>
              </ModalSheetContentDescription>
            </>
          )}

          {isRejected && (
            <>
              <ModalSheetContentTitle>
                <div className={styles.header}>Acknowledgment captured</div>
              </ModalSheetContentTitle>

              <ModalSheetContentDescription>
                <div className={styles.header}>
                  A Customer Service Representative reviewed and rejected all
                  job updates identified by{' '}
                  {isCreatedByAdmin ? 'an admin on' : 'you on'}{' '}
                  {dateFormatters.dateTimeDefault.format(latestResponseTime)}.
                </div>
              </ModalSheetContentDescription>
            </>
          )}

          {isApplyFailed && (
            <>
              <ModalSheetContentTitle>
                <div className={styles.header}>System rejected request</div>
              </ModalSheetContentTitle>

              <ModalSheetContentDescription>
                <div className={styles.header}>
                  The job scope change request was not applied to the job
                  because invalid items were added to the request on{' '}
                  {dateFormatters.dateTimeDefault.format(latestResponseTime)}.
                </div>
              </ModalSheetContentDescription>
            </>
          )}

          {isPending && (
            <>
              <ModalSheetContentTitle>
                <div className={styles.header}>Pending review</div>
              </ModalSheetContentTitle>

              <ModalSheetContentDescription>
                <div className={styles.header}>
                  {isCreatedByAdmin ? 'An admin has' : 'You have'} identified
                  services that fall outside the original job scope. Pending
                  review from a Customer Service Representative.
                </div>
              </ModalSheetContentDescription>
            </>
          )}
        </ModalSheetContentBlock>

        <ModalSheetListItem>
          <div className={styles.listItem}>
            <span>
              {isAccepted && `Accepted services (${items.length})`}
              {(isRejected || isApplyFailed) &&
                `Declined services (${items.length})`}
              {isPending && `Services (${items.length})`}
            </span>
            <span>Total</span>
          </div>
        </ModalSheetListItem>
        <div className={styles.itemsContainer}>
          {items.map((item) => (
            <ModalSheetListItem key={item.id}>
              <div className={styles.listItem}>
                <span>
                  {isAccepted && (
                    <Icon
                      className={styles.listItemIcon}
                      name="checkmark"
                      color="var(--green400)"
                      size={16}
                    />
                  )}

                  {(isRejected || isApplyFailed) && (
                    <Icon
                      className={styles.listItemIcon}
                      name="declined"
                      color="var(--red400)"
                      size={16}
                    />
                  )}
                  <strong>{item.name}</strong>
                </span>
                <span>
                  <span>{item.quantity}</span>
                </span>
              </div>
            </ModalSheetListItem>
          ))}
        </div>
        <ModalSheetSpacer />
        {isRejected && (
          <ModalSheetContentBlock>
            <div className={styles.reasonContainer}>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- the field is injected via rejectionReasonField and exposes no id to point htmlFor at */}
              <label className={styles.reasonLabel}>Reason</label>
              {rejectionReasonField}
            </div>
          </ModalSheetContentBlock>
        )}
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Close" onPress={onClose} />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
